import React, { FC, useEffect } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { useData } from '@hooks/useData';
import { formatDate, formatPrice, formatTime } from '@utils/format';
import { useGetMyProductReservationDetails } from '@services/useGetMyProductReservationDetails';
import { Table } from '@components/atoms/Table';
import { Card } from '@components/atoms/Card';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { SITE_URLS } from '@constants';
import styles from './MyProductReservationDetail.module.css';

export const MyProductReservationDetail: FC = () => {
    const { uuid } = useParams();
    const { myProductReservationDetail, isLoadingMyProductReservationDetail } = useData();
    const { request: requestMyProductReservationDetail } = useGetMyProductReservationDetails();
    const { t, i18n } = useTranslation();

    const translations = {
        title: t('my-product-reservation-detail-component.title'),
        dateLabel: t('my-product-reservation-detail-component.date.label'),
        merchantLabel: t('my-product-reservation-detail-component.merchant.label'),
        productLabel: t('my-product-reservation-detail-component.product.label'),
        quantityLabel: t('my-product-reservation-detail-component.quantity.label'),
        totalLabel: t('my-product-reservation-detail-component.total.label'),
    };

    // fetch order detail info
    useEffect(() => {
        requestMyProductReservationDetail({ uuid: uuid || '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    return (
        <MyAccountContainer
            title={translations.title}
            overrideOnDesktop={
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <Typography classes="mb-2" color="muted" fontSize="16">
                        <>{translations.title}</>
                    </Typography>
                </div>
            }>
            <>
                {!isLoadingMyProductReservationDetail && (
                    <>
                        <Typography classes="d-inline-block pe-2" fontSize="16" color="muted">
                            <>{translations.dateLabel}: </>
                        </Typography>
                        <>
                            {formatDate(myProductReservationDetail?.data?.created_at || null)}
                            <Typography tagVariant={TypographyTagVariants.span} classes="text-muted ms-2">
                                <>
                                    {myProductReservationDetail?.data?.created_at &&
                                        formatTime(myProductReservationDetail.data.created_at)}
                                </>
                            </Typography>
                        </>
                        <div className="row g-4 mt-1 mb-2">
                            <div className="col-12">
                                <Card>
                                    <Typography
                                        classes="px-4 pt-4 mx-1"
                                        fontSize="16"
                                        tagVariant={TypographyTagVariants.span /* #TODO: test UI */}>
                                        <>
                                            {`${translations.merchantLabel} `}
                                            <Anchor
                                                href={SITE_URLS.merchantDetail.replace(
                                                    ':uuid',
                                                    `${myProductReservationDetail?.data?.merchant?.uuid}`
                                                )}
                                                mode={AnchorMode.none}>
                                                <Typography classes="d-inline-block" color="primary" fontWeight="800">
                                                    {myProductReservationDetail?.data?.merchant?.name || ''}
                                                </Typography>
                                            </Anchor>
                                        </>
                                    </Typography>
                                    <Table
                                        borderless={true}
                                        hideHeaderOnMobile={true}
                                        headerCols={[
                                            { classes: 'text-start', text: translations.productLabel },
                                            { classes: 'text-center', text: translations.quantityLabel, fit: true },
                                            { classes: 'text-end', text: translations.totalLabel },
                                        ]}
                                        contentRows={[
                                            [
                                                {
                                                    content: (
                                                        <div className="d-flex gap-3 align-items-start text-decoration-none my-3">
                                                            <div className="mt-2">
                                                                {myProductReservationDetail?.data?.product_image && (
                                                                    <img
                                                                        className={styles['product-image']}
                                                                        src={myProductReservationDetail.data.product_image}
                                                                        alt={myProductReservationDetail.data.product_name}
                                                                    />
                                                                )}
                                                            </div>
                                                            <div>
                                                                <Anchor
                                                                    href={SITE_URLS.productDetail.replace(
                                                                        ':uuid',
                                                                        `${myProductReservationDetail?.data?.product_uuid}`
                                                                    )}
                                                                    mode={AnchorMode.none}>
                                                                    <Typography
                                                                        classes="mt-2 mb-1 pt-1"
                                                                        color="dark"
                                                                        fontSize="14"
                                                                        fontWeight="900"
                                                                        breakpointsStyles={{ md: { fontSize: '16' } }}>
                                                                        {myProductReservationDetail?.data?.product_name}
                                                                    </Typography>
                                                                </Anchor>
                                                                {(myProductReservationDetail?.data?.product_attributes || []).map(
                                                                    (attribute, index) => (
                                                                        <Typography
                                                                            key={`attribute-${index}`}
                                                                            classes="mb-1"
                                                                            color="dark"
                                                                            fontSize="14"
                                                                            fontWeight="400"
                                                                            breakpointsStyles={{ md: { fontSize: '16' } }}>
                                                                            {attribute}
                                                                        </Typography>
                                                                    )
                                                                )}
                                                            </div>
                                                        </div>
                                                    ),
                                                },
                                                {
                                                    content: (
                                                        <Typography fontSize="16" align="center">
                                                            {`${myProductReservationDetail?.data?.quantity}`}
                                                        </Typography>
                                                    ),
                                                },
                                                {
                                                    content: (
                                                        <Typography fontSize="16" align="right">
                                                            {formatPrice(myProductReservationDetail?.data?.total)}
                                                        </Typography>
                                                    ),
                                                },
                                            ],
                                        ]}
                                    />
                                </Card>
                            </div>
                        </div>
                    </>
                )}
            </>
        </MyAccountContainer>
    );
};
